import React, { useState, useRef } from 'react';
import { useTerminology } from '../contexts/TerminologyContext';

interface TerminologyTooltipProps {
  text: string;
  className?: string;
}

const TerminologyTooltip: React.FC<TerminologyTooltipProps> = ({ text, className = '' }) => {
  const { getTermDefinition } = useTerminology();
  const [hoveredTerm, setHoveredTerm] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const processTextWithTerminology = (inputText: string) => {
    if (!inputText) return [{ text: inputText, isTerm: false, definition: null }];

    const segments: Array<{ text: string; isTerm: boolean; definition: any }> = [];
    
    // Split text into words while preserving punctuation and spacing
    const words = inputText.split(/(\s+|[.,!?;:()])/);
    
    words.forEach((word, index) => {
      if (word.trim()) {
        const cleanWord = word.replace(/[.,!?;:()]/g, '').toLowerCase();
        const termData = getTermDefinition(cleanWord);
        
        if (termData) {
          segments.push({
            text: word,
            isTerm: true,
            definition: termData
          });
        } else {
          segments.push({
            text: word,
            isTerm: false,
            definition: null
          });
        }
      } else {
        segments.push({
          text: word,
          isTerm: false,
          definition: null
        });
      }
    });

    return segments;
  };

  const handleMouseEnter = (event: React.MouseEvent, term: string, definition: any) => {
    setHoveredTerm(term);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 8
    });
  };

  const handleMouseLeave = () => {
    setHoveredTerm(null);
  };

  const segments = processTextWithTerminology(text);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {segments.map((segment, index) => (
        <span key={index}>
          {segment.isTerm ? (
            <span
              className="underline decoration-dotted decoration-blue-500 cursor-help text-blue-700 hover:text-blue-900 transition-colors"
              onMouseEnter={(e) => handleMouseEnter(e, segment.text, segment.definition)}
              onMouseLeave={handleMouseLeave}
            >
              {segment.text}
            </span>
          ) : (
            <span>{segment.text}</span>
          )}
        </span>
      ))}
      
      {hoveredTerm && (
        <div
          className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-3 max-w-sm"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%) translateY(-100%)'
          }}
        >
          <div className="text-sm">
            <div className="font-semibold text-gray-900 mb-1">
              {segments.find(s => s.text === hoveredTerm)?.definition?.term || hoveredTerm}
              {segments.find(s => s.text === hoveredTerm)?.definition?.is_acronym && (
                <span className="ml-1 text-xs bg-blue-100 text-blue-800 px-1 rounded">
                  Acronym
                </span>
              )}
            </div>
            <div className="text-gray-700 leading-relaxed">
              {segments.find(s => s.text === hoveredTerm)?.definition?.definition}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Confidence: {segments.find(s => s.text === hoveredTerm)?.definition?.confidence_score}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminologyTooltip;